import React, { useMemo, useState } from 'react';
import {
  Check,
  Circle,
  CircleDot,
  Flag,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { isoDate } from '../../lib/time.js';
import { Select } from '../../components/controls/Select.jsx';
import { DatePicker } from '../../components/controls/DatePicker.jsx';

const LIFECYCLE_STATUS = [
  ['planned', 'Planned'],
  ['in_progress', 'In progress'],
  ['completed', 'Completed'],
];
const PAYMENT_STATUS = [
  'Planned',
  'Under discussion',
  'Upcoming',
  'Invoiced',
  'Paid',
];
const PAYMENT_DONE = new Set(['Invoiced', 'Paid']);

// Local calendar date — lifecycle / payment dates are local, so an overdue
// check against a UTC "today" would slip a day near local midnight.
const todayISO = () => isoDate(new Date());

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// Merge lifecycle milestones, payment milestones and the two schedule anchors
// into one date-sorted list, with the undated milestones held back separately.
function buildRows(project, milestones, payments) {
  const rows = [
    {
      kind: 'anchor',
      id: 'start',
      label: 'Project start',
      date: project.startDate || '',
    },
  ];
  for (const m of milestones) {
    rows.push({
      kind: 'lifecycle',
      id: m.id,
      date: m.actualDate || m.targetDate || '',
      data: m,
    });
  }
  for (const p of payments) {
    rows.push({
      kind: 'payment',
      id: p.id,
      date: p.plannedDate || '',
      data: p,
    });
  }
  rows.push({
    kind: 'anchor',
    id: 'end',
    label: 'Planned end',
    date: project.endDate || '',
  });
  const dated = rows
    .filter((r) => r.date)
    .sort((a, b) => a.date.localeCompare(b.date));
  const undated = rows.filter((r) => !r.date && r.kind !== 'anchor');
  return { dated, undated };
}

// A payment milestone seen through the lifecycle-milestone lens, so the
// "next milestone" logic can reason about both kinds of timeline event.
const paymentAsMilestone = (p) => ({
  title: p.milestoneType,
  status: PAYMENT_DONE.has(p.status) ? 'completed' : 'planned',
  targetDate: p.plannedDate || null,
  actualDate: null,
});

// The single "where are we" milestone the rest of the app reads off the
// timeline: the in-progress one, else the earliest dated event that isn't
// done, else the first open one — across lifecycle *and* payment milestones,
// matching what the timeline itself renders.
export function nextMilestone(milestones = [], payments = []) {
  const all = [...milestones, ...payments.map(paymentAsMilestone)];
  const open = all.filter((m) => m.status !== 'completed');
  const inProgress = open.find((m) => m.status === 'in_progress');
  if (inProgress) return inProgress;
  const dated = open
    .filter((m) => m.targetDate || m.actualDate)
    .sort((a, b) =>
      (a.targetDate || a.actualDate).localeCompare(
        b.targetDate || b.actualDate,
      ),
    );
  return dated[0] || open[0] || null;
}

export function nextMilestoneLabel(milestones = [], payments = []) {
  const m = nextMilestone(milestones, payments);
  if (!m) return '';
  const when = fmtDate(m.targetDate || m.actualDate);
  const base = when ? `${m.title} · ${when}` : m.title;
  return m.status === 'in_progress' ? `${base} (in progress)` : base;
}

function isOverdue(row) {
  if (!row.date || row.date >= todayISO()) return false;
  if (row.kind === 'lifecycle') return row.data.status !== 'completed';
  if (row.kind === 'payment') return !PAYMENT_DONE.has(row.data.status);
  return false;
}

function LifecycleMarker({ row }) {
  if (row.kind === 'anchor') return <Flag size={13} />;
  if (row.kind === 'payment')
    return <span className="lc-marker-dollar">$</span>;
  if (row.data.status === 'completed') return <Check size={13} />;
  if (row.data.status === 'in_progress') return <CircleDot size={13} />;
  return <Circle size={13} />;
}

export function LifecycleTimeline({
  project,
  milestones,
  payments,
  onSaveMilestones,
  onSavePayments,
  onSaveSchedule,
  // true while a payment / schedule save started from the other detail tab is
  // in flight; folds into `busy` so this tab can't send a stale write meanwhile.
  savingPayments = false,
  savingSchedule = false,
}) {
  const nextLabel = nextMilestoneLabel(milestones, payments);
  const [editing, setEditing] = useState(null); // row id, or 'new-lifecycle' / 'new-payment'
  const [confirmingId, setConfirmingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const busy = saving || savingPayments || savingSchedule;

  // Run one save; on failure keep the editor open and show why (a failed
  // post-save refresh counts as a failure — the on-screen data is now stale).
  const run = async (fn) => {
    setSaving(true);
    setError('');
    try {
      await fn();
      return true;
    } catch (err) {
      setError(err.message || 'Could not save.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const { dated, undated } = useMemo(
    () => buildRows(project, milestones, payments),
    [project, milestones, payments],
  );

  const today = todayISO();
  const todayIndex = dated.findIndex((row) => row.date > today);

  const stripId = (list) =>
    list.map((m) => ({
      title: m.title,
      status: m.status,
      targetDate: m.targetDate || null,
      actualDate: m.actualDate || null,
      description: m.description || '',
    }));
  const paymentItems = (list) =>
    list.map((p) => ({
      milestoneType: p.milestoneType,
      plannedDate: p.plannedDate || null,
      amount: p.amount || '',
      currency: p.currency || '',
      status: p.status || 'Planned',
      note: p.note || '',
    }));

  async function saveLifecycle(id, values) {
    const next =
      id === 'new-lifecycle'
        ? [...milestones, values]
        : milestones.map((m) => (m.id === id ? { ...m, ...values } : m));
    if (await run(() => onSaveMilestones(stripId(next)))) setEditing(null);
  }

  async function savePayment(id, values) {
    const next =
      id === 'new-payment'
        ? [...payments, values]
        : payments.map((p) => (p.id === id ? { ...p, ...values } : p));
    if (await run(() => onSavePayments(paymentItems(next)))) setEditing(null);
  }

  async function saveAnchor(which, date) {
    const ok = await run(() =>
      onSaveSchedule({
        startDate: which === 'start' ? date || null : project.startDate || null,
        endDate: which === 'end' ? date || null : project.endDate || null,
      }),
    );
    if (ok) setEditing(null);
  }

  async function removeLifecycle(id) {
    const ok = await run(() =>
      onSaveMilestones(stripId(milestones.filter((m) => m.id !== id))),
    );
    if (ok) setConfirmingId(null);
  }

  async function removePayment(id) {
    const ok = await run(() =>
      onSavePayments(paymentItems(payments.filter((p) => p.id !== id))),
    );
    if (ok) setConfirmingId(null);
  }

  function renderRow(row) {
    const overdue = isOverdue(row);
    if (editing === row.id) {
      return (
        <li className="lc-row is-editing" key={row.id}>
          {row.kind === 'anchor' && (
            <AnchorForm
              row={row}
              busy={busy}
              onCancel={() => setEditing(null)}
              onSave={(date) => saveAnchor(row.id, date)}
            />
          )}
          {row.kind === 'lifecycle' && (
            <LifecycleForm
              value={row.data}
              busy={busy}
              onCancel={() => setEditing(null)}
              onSave={(values) => saveLifecycle(row.id, values)}
            />
          )}
          {row.kind === 'payment' && (
            <PaymentForm
              value={row.data}
              busy={busy}
              onCancel={() => setEditing(null)}
              onSave={(values) => savePayment(row.id, values)}
            />
          )}
        </li>
      );
    }
    return (
      <li
        className={`lc-row lc-${row.kind}${overdue ? ' is-overdue' : ''}`}
        key={row.id}
      >
        <span className="lc-marker">
          <LifecycleMarker row={row} />
        </span>
        <div className="lc-body">
          <div className="lc-title">
            {row.kind === 'anchor' && row.label}
            {row.kind === 'lifecycle' && row.data.title}
            {row.kind === 'payment' && row.data.milestoneType}
          </div>
          <div className="lc-meta">
            <span>{fmtDate(row.date) || 'No date'}</span>
            {row.kind === 'payment' &&
              (row.data.amount || row.data.currency) && (
                <span>
                  {[row.data.amount, row.data.currency]
                    .filter(Boolean)
                    .join(' ')}
                </span>
              )}
            {row.kind === 'lifecycle' && row.data.status === 'in_progress' && (
              <span className="project-status-badge status-paused">
                In progress
              </span>
            )}
            {row.kind === 'payment' && row.data.status && (
              <span className="project-status-badge">{row.data.status}</span>
            )}
            {overdue && <span className="lc-overdue-tag">Overdue</span>}
          </div>
          {row.kind === 'lifecycle' && row.data.description && (
            <p className="lc-note">{row.data.description}</p>
          )}
          {row.kind === 'payment' && row.data.note && (
            <p className="lc-note">{row.data.note}</p>
          )}
        </div>

        {confirmingId === row.id ? (
          <div className="editable-list-confirm">
            <span>Remove?</span>
            <button
              type="button"
              className="text-button danger-text"
              disabled={busy}
              onClick={() =>
                row.kind === 'payment'
                  ? removePayment(row.id)
                  : removeLifecycle(row.id)
              }
            >
              Remove
            </button>
            <button
              type="button"
              className="text-button"
              onClick={() => setConfirmingId(null)}
            >
              Keep
            </button>
          </div>
        ) : (
          <div className="editable-list-item-actions">
            <button
              type="button"
              className="icon-button"
              aria-label="Edit"
              onClick={() => setEditing(row.id)}
            >
              <Pencil size={15} />
            </button>
            {row.kind !== 'anchor' && (
              <button
                type="button"
                className="icon-button"
                aria-label="Remove"
                onClick={() => setConfirmingId(row.id)}
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        )}
      </li>
    );
  }

  return (
    <section className="card memory-card memory-lifecycle">
      <div className="card-head">
        <div>
          <h2>Project lifecycle</h2>
          <p className="section-subtitle">
            {nextLabel ? `Next: ${nextLabel}` : 'No milestones yet'}
          </p>
        </div>
        <div className="lc-add-buttons">
          <button
            type="button"
            className="secondary small"
            onClick={() => setEditing('new-lifecycle')}
          >
            <Plus size={13} /> Milestone
          </button>
          <button
            type="button"
            className="secondary small"
            onClick={() => setEditing('new-payment')}
          >
            <Plus size={13} /> Payment
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {editing === 'new-lifecycle' && (
        <div className="lc-new">
          <div className="history-event-heading">New milestone</div>
          <LifecycleForm
            value={{ status: 'planned' }}
            busy={busy}
            onCancel={() => setEditing(null)}
            onSave={(values) => saveLifecycle('new-lifecycle', values)}
          />
        </div>
      )}
      {editing === 'new-payment' && (
        <div className="lc-new">
          <div className="history-event-heading">New payment milestone</div>
          <PaymentForm
            value={{ status: 'Planned' }}
            busy={busy}
            onCancel={() => setEditing(null)}
            onSave={(values) => savePayment('new-payment', values)}
          />
        </div>
      )}

      {dated.length === 0 && undated.length === 0 ? (
        <p className="muted-text">
          No dates yet — set a start date on Project Info, or add a milestone.
        </p>
      ) : (
        <ol className="lifecycle-timeline">
          {dated.map((row, i) => (
            <React.Fragment key={row.id}>
              {i === todayIndex && (
                <li className="lc-today" aria-hidden="true">
                  <span>Today</span>
                </li>
              )}
              {renderRow(row)}
            </React.Fragment>
          ))}
          {todayIndex === -1 && dated.length > 0 && (
            <li className="lc-today" aria-hidden="true">
              <span>Today</span>
            </li>
          )}
        </ol>
      )}

      {undated.length > 0 && (
        <div className="lc-undated">
          <span className="lifecycle-label">No date set</span>
          <ol className="lifecycle-timeline">{undated.map(renderRow)}</ol>
        </div>
      )}
    </section>
  );
}

function Actions({ busy, onCancel, saveLabel }) {
  return (
    <div className="editable-section-actions">
      <button
        type="button"
        className="secondary"
        onClick={onCancel}
        disabled={busy}
      >
        Cancel
      </button>
      <button className="primary" type="submit" disabled={busy}>
        {busy ? 'Saving…' : saveLabel}
      </button>
    </div>
  );
}

function AnchorForm({ row, busy, onCancel, onSave }) {
  const [date, setDate] = useState(row.date || '');
  return (
    <form
      className="interaction-form lc-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(date);
      }}
    >
      <label className="full">
        <span>{row.label}</span>
        <DatePicker ariaLabel={row.label} value={date} onChange={setDate} />
      </label>
      <Actions busy={busy} onCancel={onCancel} saveLabel="Save" />
    </form>
  );
}

function LifecycleForm({ value, busy, onCancel, onSave }) {
  const [form, setForm] = useState({
    title: value.title || '',
    status: value.status || 'planned',
    targetDate: value.targetDate || '',
    actualDate: value.actualDate || '',
    description: value.description || '',
  });
  const set = (patch) => setForm((c) => ({ ...c, ...patch }));
  return (
    <form
      className="interaction-form lc-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          title: form.title.trim(),
          status: form.status,
          targetDate: form.targetDate || null,
          actualDate: form.actualDate || null,
          description: form.description.trim(),
        });
      }}
    >
      <div className="form-grid">
        <label>
          <span>Milestone</span>
          <input
            required
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
          />
        </label>
        <label>
          <span>Status</span>
          <Select
            ariaLabel="Status"
            value={form.status}
            onChange={(status) => set({ status })}
            options={LIFECYCLE_STATUS.map(([v, l]) => ({ value: v, label: l }))}
          />
        </label>
        <label>
          <span>Target date</span>
          <DatePicker
            ariaLabel="Target date"
            value={form.targetDate}
            onChange={(targetDate) => set({ targetDate })}
          />
        </label>
        <label>
          <span>Actual date</span>
          <DatePicker
            ariaLabel="Actual date"
            value={form.actualDate}
            onChange={(actualDate) => set({ actualDate })}
          />
        </label>
      </div>
      <label className="full">
        <span>Description</span>
        <textarea
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </label>
      <Actions busy={busy} onCancel={onCancel} saveLabel="Save" />
    </form>
  );
}

function PaymentForm({ value, busy, onCancel, onSave }) {
  const [form, setForm] = useState({
    milestoneType: value.milestoneType || '',
    plannedDate: value.plannedDate || '',
    amount: value.amount || '',
    currency: value.currency || '',
    status: value.status || 'Planned',
    note: value.note || '',
  });
  const set = (patch) => setForm((c) => ({ ...c, ...patch }));
  return (
    <form
      className="interaction-form lc-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          milestoneType: form.milestoneType.trim(),
          plannedDate: form.plannedDate || null,
          amount: form.amount.trim(),
          currency: form.currency.trim(),
          status: form.status,
          note: form.note.trim(),
        });
      }}
    >
      <div className="form-grid">
        <label>
          <span>Milestone</span>
          <input
            required
            value={form.milestoneType}
            onChange={(e) => set({ milestoneType: e.target.value })}
          />
        </label>
        <label>
          <span>Planned date</span>
          <DatePicker
            ariaLabel="Planned date"
            value={form.plannedDate}
            onChange={(plannedDate) => set({ plannedDate })}
          />
        </label>
        <label>
          <span>Value</span>
          <input
            value={form.amount}
            onChange={(e) => set({ amount: e.target.value })}
          />
        </label>
        <label>
          <span>Currency</span>
          <input
            value={form.currency}
            onChange={(e) => set({ currency: e.target.value })}
          />
        </label>
        <label>
          <span>Status</span>
          <Select
            ariaLabel="Status"
            value={form.status}
            onChange={(status) => set({ status })}
            options={PAYMENT_STATUS.map((s) => ({ value: s, label: s }))}
          />
        </label>
      </div>
      <label className="full">
        <span>Note</span>
        <input
          value={form.note}
          onChange={(e) => set({ note: e.target.value })}
        />
      </label>
      <Actions busy={busy} onCancel={onCancel} saveLabel="Save" />
    </form>
  );
}
